import { Map } from 'mapbox-gl';
import { BoundingBox } from '../../../../../types';

export const updateBoundingBoxStyles = (
  mapRef: React.RefObject<Map>,
  boundingBox: BoundingBox | null // Allow null for removal
): void => {
  if (!boundingBox) {
    removeBoundingBox(mapRef);
    return;
  }

  const { north, south, east, west } = boundingBox;
  
  // Create circle points based on center and radius
  const createCircle = () => {
    const points: [number, number][] = [];
    const center: [number, number] = [
      (east + west) / 2,
      (north + south) / 2
    ];
    
    // Use the larger of width/height to ensure all points are contained
    const radius = Math.max(
      Math.abs(east - west) / 2,
      Math.abs(north - south) / 2
    );
    
    // Generate circle points
    const segments = 64; // More segments for smoother circle
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * 2 * Math.PI;
      const x = center[0] + radius * Math.cos(angle);
      const y = center[1] + radius * Math.sin(angle);
      points.push([x, y]);
    }
    
    return points;
  };

  const applyStyles = () => {
    if (!mapRef.current) return;

    // Remove existing layers
    removeBoundingBox(mapRef);
    if (mapRef.current.getLayer('maine')) {
      mapRef.current.removeLayer('maine');
    }
    if (mapRef.current.getLayer('outline')) {
      mapRef.current.removeLayer('outline');
    }

    // Create a new source specifically for the circle
    if (!mapRef.current.getSource('boundingBox')) {
      mapRef.current.addSource('boundingBox', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [createCircle()]
            }
          }]
        }
      });
    } else {
      (mapRef.current.getSource('boundingBox') as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [createCircle()]
          }
        }]
      });
    }

    // Add layers using the new source
    mapRef.current.addLayer({
      id: 'boundingBox-fill',
      type: 'fill',
      source: 'boundingBox',
      paint: {
        'fill-color': '#77B5F2',
        'fill-opacity': 0.2,
        'fill-antialias': true
      }
    });

    mapRef.current.addLayer({
      id: 'boundingBox-outline',
      type: 'line',
      source: 'boundingBox',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#002E53',
        'line-width': 2
      }
    });
  };

  if (mapRef.current?.isStyleLoaded()) {
    applyStyles();
  } else {
    mapRef.current?.once('style.load', () => {
      applyStyles();
    });
  }
};

export const removeBoundingBox = (mapRef: React.RefObject<Map>): void => {
  if (!mapRef.current) return;

  // Remove layers
  if (mapRef.current.getLayer('boundingBox-fill')) {
    mapRef.current.removeLayer('boundingBox-fill');
  }
  if (mapRef.current.getLayer('boundingBox-outline')) {
    mapRef.current.removeLayer('boundingBox-outline');
  }

  // Remove source
  if (mapRef.current.getSource('boundingBox')) {
    mapRef.current.removeSource('boundingBox');
  }
};
